import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { MyPartyItem, EdiScopeDirection } from '../../services/api/activePartyService';
import { fetchMyBuyers, fetchSuppliersForBuyer } from '../thunks/activePartyThunks';
import { resolveActivePartyId } from './activePartyResolution';
import { clearActivePartyStorage } from './activePartyStorage';
import { setActiveTenant } from './tenantSlice';
import { logoutSuccess, logoutFailure } from './authSlice';

/**
 * Active-party top-bar state. Holds the active buyer/supplier as full objects
 * (so the UI shows names, not ids), the user's selectable buyer list, and the
 * per-buyer supplier lists cached by buyer id. Spec: active-party-top-bar.
 */
export interface ActivePartyState {
  isUnrestricted: boolean;
  affiliationType: string | null;
  buyers: MyPartyItem[];
  suppliersByBuyerId: Record<number, MyPartyItem[]>;
  activeBuyer: MyPartyItem | null;
  activeSupplier: MyPartyItem | null;
  tenantDefaultBuyerPartyId: number | null;
  tenantDefaultSupplierPartyId: number | null;
  loadedForTenantId: number | null;
  isLoadingBuyers: boolean;
  isLoadingSuppliers: boolean;
  error: string | null;
  // ── My EDI scope (spec: edi-admin-and-scoped-party-selector) ──
  direction: EdiScopeDirection;
  hasGlobalEdiRead: boolean;
  myBuyerParties: MyPartyItem[];
  mySupplierParties: MyPartyItem[];
}

const initialState: ActivePartyState = {
  isUnrestricted: false,
  affiliationType: null,
  buyers: [],
  suppliersByBuyerId: {},
  activeBuyer: null,
  activeSupplier: null,
  tenantDefaultBuyerPartyId: null,
  tenantDefaultSupplierPartyId: null,
  loadedForTenantId: null,
  isLoadingBuyers: false,
  isLoadingSuppliers: false,
  error: null,
  direction: 'unrestricted',
  hasGlobalEdiRead: false,
  myBuyerParties: [],
  mySupplierParties: [],
};

const activePartySlice = createSlice({
  name: 'activeParty',
  initialState,
  reducers: {
    /** Explicit user pick of the active buyer — clears the supplier (Req 1.6). */
    setActiveBuyer(state, action: PayloadAction<MyPartyItem | null>) {
      state.activeBuyer = action.payload;
      state.activeSupplier = null;
    },
    setActiveSupplier(state, action: PayloadAction<MyPartyItem | null>) {
      state.activeSupplier = action.payload;
    },
    /** Clears both selections without touching the loaded lists. */
    clearActiveParty(state) {
      state.activeBuyer = null;
      state.activeSupplier = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyBuyers.pending, (state) => {
        state.isLoadingBuyers = true;
        state.error = null;
      })
      .addCase(fetchMyBuyers.fulfilled, (state, action) => {
        const { response, persistedBuyerId } = action.payload;
        // True when this fulfilled call is a refresh (e.g. the search
        // effect re-fetching the buyer list for an unrestricted user) —
        // NOT the initial load after a tenant switch. We compare against
        // `loadedForTenantId` BEFORE writing it, so the initial-load
        // value (null) makes this `false`; subsequent fetches with
        // matching tenantId make it `true`.
        const isRefresh = state.loadedForTenantId === action.meta.arg.tenantId;

        state.isLoadingBuyers = false;
        state.isUnrestricted = response.isUnrestricted;
        state.affiliationType = response.affiliationType;
        state.buyers = response.buyers;
        state.tenantDefaultBuyerPartyId = response.tenantDefaultBuyerPartyId;
        state.tenantDefaultSupplierPartyId = response.tenantDefaultSupplierPartyId;
        state.loadedForTenantId = action.meta.arg.tenantId;
        // My EDI scope (spec: edi-admin-and-scoped-party-selector) — scope facts,
        // set on every fulfilled (initial + search refresh).
        state.direction = response.direction;
        state.hasGlobalEdiRead = response.hasGlobalEdiRead;
        state.myBuyerParties = response.myBuyerParties;
        state.mySupplierParties = response.mySupplierParties;

        if (isRefresh) {
          // Search refresh — refresh the list but preserve the user's
          // current selection. If the active buyer is still present in
          // the refreshed list, take the updated reference (display
          // fields may have changed); otherwise keep the prior
          // reference so the top bar still shows the choice. The
          // previously-restored activeSupplier is intentionally
          // preserved — a list refresh did not change the selection.
          if (state.activeBuyer != null) {
            const updated = response.buyers.find(
              (b) => b.partyId === state.activeBuyer!.partyId
            );
            if (updated) state.activeBuyer = updated;
          }
        } else {
          // Initial load (post-tenant-switch or first fetch). Resolve
          // the active buyer from persistence; a new buyer invalidates
          // the prior supplier.
          const resolvedId = resolveActivePartyId(
            persistedBuyerId,
            response.tenantDefaultBuyerPartyId,
            response.buyers
          );
          state.activeBuyer =
            resolvedId != null
              ? response.buyers.find((b) => b.partyId === resolvedId) ?? null
              : null;
          state.activeSupplier = null;
        }
      })
      .addCase(fetchMyBuyers.rejected, (state, action) => {
        state.isLoadingBuyers = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Failed to load buyers';
      });

    builder
      .addCase(fetchSuppliersForBuyer.pending, (state) => {
        state.isLoadingSuppliers = true;
        state.error = null;
      })
      .addCase(fetchSuppliersForBuyer.fulfilled, (state, action) => {
        const { buyerPartyId, suppliers, persistedSupplierId } = action.payload;
        state.isLoadingSuppliers = false;
        state.suppliersByBuyerId[buyerPartyId] = suppliers;

        // Only resolve the supplier if these suppliers are for the current buyer.
        if (state.activeBuyer?.partyId === buyerPartyId) {
          const resolvedId = resolveActivePartyId(
            persistedSupplierId,
            state.tenantDefaultSupplierPartyId,
            suppliers
          );
          state.activeSupplier =
            resolvedId != null ? suppliers.find((s) => s.partyId === resolvedId) ?? null : null;
        }
      })
      .addCase(fetchSuppliersForBuyer.rejected, (state, action) => {
        state.isLoadingSuppliers = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? 'Failed to load suppliers';
      });

    // Party ids are tenant-scoped — a tenant switch fully resets state. A
    // tenant switch keeps the local-storage mirror (per-tenant recall, Req 5.3);
    // logout additionally drops the mirror so a new user starts clean.
    const resetOnLogout = () => {
      clearActivePartyStorage();
      return initialState;
    };
    builder
      .addCase(setActiveTenant, () => initialState)
      .addCase(logoutSuccess, resetOnLogout)
      .addCase(logoutFailure, resetOnLogout);
  },
});

export const { setActiveBuyer, setActiveSupplier, clearActiveParty } = activePartySlice.actions;
export default activePartySlice.reducer;
